import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzIbDataService,
  IncomeTaxEntity,
  JaarrekeningVenWKostensoort,
  Onderneming,
  Ondernemingsvorm,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormControl, FormGroup} from '@angular/forms';
import {filter, Observable, shareReplay, take, tap} from 'rxjs';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type BedragGroup = FormGroup<{ bedrag: FormControl<number | null> }>;

type BedragKostensoortGroup = FormGroup<{
  bedrag: FormControl<number | null>;
  kostensoort: FormControl<JaarrekeningVenWKostensoort | null>
}>;

type TussentellingenGroup = FormGroup<{
  tussentellingOpbrengsten: FormControl<number | null>;
  tussentellingKosten: FormControl<number | null>;
  tussentellingRest: FormControl<number | null>;
  winstResultatenrekening: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-winst-en-verlies',
  templateUrl: './winst-en-verlies.component.html',
  styleUrls: ['./winst-en-verlies.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class WinstEnVerliesComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  public isPartner = false;
  public index = 0;
  public indexOnderneming = 0;
  public heeftOndernemingen = false;
  public heeftSamenwerkingsverband = false;
  public heeftWuoStaking = false

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly indexOnderneming$: Observable<number> =
    this.dataService.indexOnderneming$.pipe(
      tap(data => {
        this.indexOnderneming = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public wuoOpbrengstenFg: BedragGroup = this.createJaarrekeningGroup();
  public financieleBatenFg: BedragGroup = this.createJaarrekeningGroup();
  public buitengewoneBatenFg: BedragGroup = this.createJaarrekeningGroup();
  public wuoVrijgesteldeWinstbestanddelenFg: BedragGroup = this.createJaarrekeningGroup();
  public wuoStakingswinstenFg: BedragGroup = this.createJaarrekeningGroup();
  public wuoInkoopkostenFg: BedragKostensoortGroup = this.createJaarrekeningGroup(JaarrekeningVenWKostensoort.INKOOPKOSTEN);
  public wuoVerkoopkostenFg: BedragKostensoortGroup = this.createJaarrekeningGroup(JaarrekeningVenWKostensoort.VERKOOPKOSTEN);
  public wuoPersoneelskostenFg: BedragKostensoortGroup = this.createJaarrekeningGroup(JaarrekeningVenWKostensoort.PERSONEELSKOSTEN);
  public wuoHuisvestingskostenFg: BedragKostensoortGroup = this.createJaarrekeningGroup(JaarrekeningVenWKostensoort.HUISVESTINGSKOSTEN);
  public wuoAutokostenFg: BedragKostensoortGroup = this.createJaarrekeningGroup(JaarrekeningVenWKostensoort.AUTOKOSTEN);
  public wuoAfschrijvingenFg: BedragKostensoortGroup = this.createJaarrekeningGroup(JaarrekeningVenWKostensoort.AFSCHRIJVINGEN);
  public wuoOverigekostenFg: BedragKostensoortGroup = this.createJaarrekeningGroup(JaarrekeningVenWKostensoort.OVERIGE);
  public wuoFinancieleLastenFg: BedragGroup = this.createJaarrekeningGroup();
  public wuoBuitengewoneLastenFg: BedragGroup = this.createJaarrekeningGroup();

  public tussentellingenFg: TussentellingenGroup = new FormGroup<{
    tussentellingOpbrengsten: FormControl<number | null>;
    tussentellingKosten: FormControl<number | null>;
    tussentellingRest: FormControl<number | null>;
    winstResultatenrekening: FormControl<number | null>;
  }>({
    tussentellingOpbrengsten: new FormControl<number | null>(null),
    tussentellingKosten: new FormControl<number | null>(null),
    tussentellingRest: new FormControl<number | null>(null),
    winstResultatenrekening: new FormControl<number | null>(null)
  });

  private createJaarrekeningGroup(): BedragGroup;
  private createJaarrekeningGroup(kostensoort: JaarrekeningVenWKostensoort): BedragKostensoortGroup;
  private createJaarrekeningGroup(kostensoort?: JaarrekeningVenWKostensoort): BedragGroup | BedragKostensoortGroup {
    if (kostensoort !== undefined) {
      return new FormGroup<{
        bedrag: FormControl<number | null>;
        kostensoort: FormControl<JaarrekeningVenWKostensoort | null>
      }>({
        bedrag: new FormControl<number | null>(null),
        kostensoort: new FormControl<JaarrekeningVenWKostensoort | null>(kostensoort)
      });
    } else {
      return new FormGroup<{ bedrag: FormControl<number | null> }>({
        bedrag: new FormControl<number | null>(null)
      });
    }
  }

  ngOnInit(): void {
    this.indexOnderneming$.subscribe();
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
    [
      this.wuoOpbrengstenFg,
      this.financieleBatenFg,
      this.buitengewoneBatenFg,
      this.wuoVrijgesteldeWinstbestanddelenFg,
      this.wuoStakingswinstenFg,
      this.wuoInkoopkostenFg,
      this.wuoVerkoopkostenFg,
      this.wuoPersoneelskostenFg,
      this.wuoHuisvestingskostenFg,
      this.wuoAutokostenFg,
      this.wuoAfschrijvingenFg,
      this.wuoOverigekostenFg,
      this.wuoFinancieleLastenFg,
      this.wuoBuitengewoneLastenFg
    ].forEach(fg => {
      const bedragControl = fg.controls.bedrag;
      bedragControl?.valueChanges.subscribe(val => {
        if (val === null || (typeof val === 'string' && val === '')) {
          fg.reset({}, {emitEvent: false});
          this.saveForm();
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
    this.dataService.calculateLatestIncomeTaxReturn().subscribe();
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: any): void {
    this.resetFormGroups();
    this.heeftOndernemingen = false;
    this.heeftSamenwerkingsverband = false;
    this.heeftWuoStaking = false
    const ondernemingen: Onderneming[] = details.box1?.winstUitOnderneming?.ondernemingen || [];
    this.patchProfitLossFormgroup(ondernemingen);
  }

  private resetFormGroups() {
    this.wuoOpbrengstenFg.reset({}, {emitEvent: false});
    this.financieleBatenFg.reset({}, {emitEvent: false});
    this.buitengewoneBatenFg.reset({}, {emitEvent: false});
    this.wuoVrijgesteldeWinstbestanddelenFg.reset({}, {emitEvent: false});
    this.wuoStakingswinstenFg.reset({}, {emitEvent: false});
    this.wuoInkoopkostenFg.reset({}, {emitEvent: false});
    this.wuoVerkoopkostenFg.reset({}, {emitEvent: false});
    this.wuoPersoneelskostenFg.reset({}, {emitEvent: false});
    this.wuoHuisvestingskostenFg.reset({}, {emitEvent: false});
    this.wuoAutokostenFg.reset({}, {emitEvent: false});
    this.wuoAfschrijvingenFg.reset({}, {emitEvent: false});
    this.wuoOverigekostenFg.reset({}, {emitEvent: false});
    this.wuoFinancieleLastenFg.reset({}, {emitEvent: false});
    this.wuoBuitengewoneLastenFg.reset({}, {emitEvent: false});
  }

  private patchProfitLossFormgroup(ondernemingen: Onderneming[]): void {
    // Reset all groups to clear previous values
    this.resetFormGroups();
    const onderneming = ondernemingen[this.indexOnderneming];
    if (!onderneming) return;
    this.heeftOndernemingen = true;
    this.heeftSamenwerkingsverband = onderneming.ondernemingsvorm !== Ondernemingsvorm.EENMANSZAAK;
    this.heeftWuoStaking = onderneming.datumStaking !== undefined;

    // Patch single-value groups
    this.wuoOpbrengstenFg.patchValue(onderneming.wuoOpbrengsten?.[0] ?? {}, {emitEvent: false});
    this.financieleBatenFg.patchValue(onderneming.financieleBaten?.[0] ?? {}, {emitEvent: false});
    this.buitengewoneBatenFg.patchValue(onderneming.buitengewoneBaten?.[0] ?? {}, {emitEvent: false});
    this.wuoVrijgesteldeWinstbestanddelenFg.patchValue(onderneming.wuoVrijgesteldeWinstbestanddelen?.[0] ?? {}, {emitEvent: false});
    this.wuoStakingswinstenFg.patchValue(onderneming.wuoStakingswinsten?.[0] ?? {}, {emitEvent: false});

    // Patch kosten groups by kostensoort
    const findKosten = (soort: JaarrekeningVenWKostensoort) => onderneming.wuoKosten?.find(item => item.kostensoort === soort) ?? {};
    this.wuoInkoopkostenFg.patchValue(findKosten(JaarrekeningVenWKostensoort.INKOOPKOSTEN), {emitEvent: false});
    this.wuoVerkoopkostenFg.patchValue(findKosten(JaarrekeningVenWKostensoort.VERKOOPKOSTEN), {emitEvent: false});
    this.wuoPersoneelskostenFg.patchValue(findKosten(JaarrekeningVenWKostensoort.PERSONEELSKOSTEN), {emitEvent: false});
    this.wuoHuisvestingskostenFg.patchValue(findKosten(JaarrekeningVenWKostensoort.HUISVESTINGSKOSTEN), {emitEvent: false});
    this.wuoAutokostenFg.patchValue(findKosten(JaarrekeningVenWKostensoort.AUTOKOSTEN), {emitEvent: false});
    this.wuoAfschrijvingenFg.patchValue(findKosten(JaarrekeningVenWKostensoort.AFSCHRIJVINGEN), {emitEvent: false});
    this.wuoOverigekostenFg.patchValue(findKosten(JaarrekeningVenWKostensoort.OVERIGE), {emitEvent: false});

    // Patch overige baten/lasten
    this.wuoFinancieleLastenFg.patchValue(onderneming.financieleLasten?.[0] ?? {}, {emitEvent: false});
    this.wuoBuitengewoneLastenFg.patchValue(onderneming.buitengewoneLasten?.[0] ?? {}, {emitEvent: false});
  }

  saveForm(): void {
    // Gather all relevant values from the form groups, ensuring arrays are empty if all fields are cleared
    const updatedOnderneming: Partial<Onderneming> = {
      wuoOpbrengsten: this.groupToArray(this.wuoOpbrengstenFg.getRawValue()),
      financieleBaten: this.groupToArray(this.financieleBatenFg.getRawValue()),
      buitengewoneBaten: this.groupToArray(this.buitengewoneBatenFg.getRawValue()),
      wuoVrijgesteldeWinstbestanddelen: this.groupToArray(this.wuoVrijgesteldeWinstbestanddelenFg.getRawValue()),
      wuoStakingswinsten: this.groupToArray(this.wuoStakingswinstenFg.getRawValue()),
      wuoKosten: [
        ...this.groupToArray(this.wuoInkoopkostenFg.getRawValue(), JaarrekeningVenWKostensoort.INKOOPKOSTEN),
        ...this.groupToArray(this.wuoVerkoopkostenFg.getRawValue(), JaarrekeningVenWKostensoort.VERKOOPKOSTEN),
        ...this.groupToArray(this.wuoPersoneelskostenFg.getRawValue(), JaarrekeningVenWKostensoort.PERSONEELSKOSTEN),
        ...this.groupToArray(this.wuoHuisvestingskostenFg.getRawValue(), JaarrekeningVenWKostensoort.HUISVESTINGSKOSTEN),
        ...this.groupToArray(this.wuoAutokostenFg.getRawValue(), JaarrekeningVenWKostensoort.AUTOKOSTEN),
        ...this.groupToArray(this.wuoAfschrijvingenFg.getRawValue(), JaarrekeningVenWKostensoort.AFSCHRIJVINGEN),
        ...this.groupToArray(this.wuoOverigekostenFg.getRawValue(), JaarrekeningVenWKostensoort.OVERIGE)
      ],
      financieleLasten: this.groupToArray(this.wuoFinancieleLastenFg.getRawValue()),
      buitengewoneLasten: this.groupToArray(this.wuoBuitengewoneLastenFg.getRawValue())
    };
    // Clean all empty arrays, arrays of empty objects, and empty objects from the outgoing data
    const cleanedOnderneming = this.dataService.deepClean(updatedOnderneming);
    const idx = this.indexOnderneming;
    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }
      const update = (aangifte: any) => {
        const existingOndernemingen = aangifte.box1?.winstUitOnderneming?.ondernemingen || [];
        const updatedOndernemingen = existingOndernemingen.map((onderneming: Onderneming, i: number) => {
          if (i !== idx) return onderneming;
          // Only update fields that are present and not null/undefined
          return {
            ...onderneming,
            ...cleanedOnderneming,
          };
        });
        return {
          ...aangifte,
          box1: {
            ...aangifte.box1,
            winstUitOnderneming: {
              ...aangifte.box1?.winstUitOnderneming,
              ondernemingen: updatedOndernemingen,
            },
          },
        };
      };
      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!)
      } : update(currentEntity.aangifte);
      return this.dataService.deepClean({
        ...currentEntity,
        aangifte: updatedAangifte,
      }) as IncomeTaxEntity;
    });
  }

  public getTussentellingOpbrengsten(): any {
    const wuoOpbrengsten = this.wuoOpbrengstenFg.get('bedrag')?.value || 0;
    const financieleBaten = this.financieleBatenFg.get('bedrag')?.value || 0;
    const buitengewoneBaten = this.buitengewoneBatenFg.get('bedrag')?.value || 0;
    // const wuoVrijgesteldeWinstbestanddelen = this.wuoVrijgesteldeWinstbestanddelenFg.get('bedrag')?.value || 0;
    const wuoStakingswinsten = this.wuoStakingswinstenFg.get('bedrag')?.value || 0;
    const sum = wuoOpbrengsten + financieleBaten + buitengewoneBaten + wuoStakingswinsten;
    this.tussentellingenFg.patchValue({tussentellingOpbrengsten: sum}, {emitEvent: false});
    return sum;
  }

  public getTussentellingKosten(): any {
    const wuoInkoopkosten = this.wuoInkoopkostenFg.get('bedrag')?.value || 0;
    const wuoVerkoopkosten = this.wuoVerkoopkostenFg.get('bedrag')?.value || 0;
    const wuoPersoneelskosten = this.wuoPersoneelskostenFg.get('bedrag')?.value || 0;
    const wuoHuisvestingskosten = this.wuoHuisvestingskostenFg.get('bedrag')?.value || 0;
    const wuoAutokosten = this.wuoAutokostenFg.get('bedrag')?.value || 0;
    const wuoAfschrijvingen = this.wuoAfschrijvingenFg.get('bedrag')?.value || 0;
    const wuoOverigekosten = this.wuoOverigekostenFg.get('bedrag')?.value || 0;
    const wuoFinancieleLasten = this.wuoFinancieleLastenFg.get('bedrag')?.value || 0;
    const wuoBuitengewoneLasten = this.wuoBuitengewoneLastenFg.get('bedrag')?.value || 0;
    const sum = wuoInkoopkosten + wuoVerkoopkosten + wuoPersoneelskosten + wuoHuisvestingskosten + wuoAutokosten + wuoAfschrijvingen + wuoOverigekosten + wuoFinancieleLasten + wuoBuitengewoneLasten;
    this.tussentellingenFg.patchValue({tussentellingKosten: sum}, {emitEvent: false});
    return sum;
  }

  public getWinstResultatenrekening(): any {
    const wuoOpbrengsten = this.tussentellingenFg.get('tussentellingOpbrengsten')?.value ?? 0;
    const wuoKosten = this.tussentellingenFg.get('tussentellingKosten')?.value ?? 0;
    const sum = wuoOpbrengsten - wuoKosten;
    this.tussentellingenFg.patchValue({winstResultatenrekening: sum}, {emitEvent: false});
    return sum;
  }

  private stripNulls<T extends object>(obj: T): Partial<T> {
    const result: any = {};
    for (const [k, v] of Object.entries(obj)) {
      if (k === 'kostensoort') {
        // Always keep kostensoort, even if null or empty
        result[k] = v;
      } else if (v !== null && v !== '') {
        result[k] = v;
      }
    }
    return result;
  }

  private groupToArray(obj: any, kostensoort?: JaarrekeningVenWKostensoort): any[] {
    const stripped = this.stripNulls(obj);
    // If this is a kostensoort group, always set kostensoort
    if (kostensoort !== undefined) {
      stripped['kostensoort'] = kostensoort;
    }
    // If all fields are cleared, stripped is an empty object
    return Object.keys(stripped).length === 0 ? [] : [stripped];
  }
}
